import { useMemo } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import {
  Button,
  Drawer,
  Tabs,
  Separator,
  Grid,
  useToastAlert,
  IOption,
} from '@design-systems/canvas-ui'
import Icon, { PlusCircleIcon } from '@design-systems/react-icons'
import { useCallback, useState } from 'react'
import { useMutation } from '@apollo/client/react'
import { characterCount } from '@plm-core/utils'
import ControlledFormField from 'components/form/ControlledFormField'
import UserRolesCombobox from 'users/components/UserRolesCombobox'
import UserSearchAutocomplete, {
  type UserSearchScope,
} from 'users/components/UserSearchAutocomplete'
import TeamMemberList from '../TeamMemberList'
import { extractDirtyFormValues } from 'components/form/utils/formHelpers'
import {
  UPDATE_TEAM_MUTATION,
  TeamUpdateInput,
} from 'graphql/mutations/teams/teamMutations'

type FormTeamMember = {
  id: string
  firstName: string
  lastName: string
  companyName: string
  isRemoved: boolean
  lanId: string
  roles: Array<{
    code: string
    description: string
  }>
}

type Inputs = {
  teamName: string
  statusId: number
  members: Array<FormTeamMember>
  newMember: (IOption & { data: any }) | null
  newRoles: Array<any>
}

type Props = {
  team: any
  isOpen: boolean
  onRequestClose: () => void
  refetchTeam?: () => Promise<any>
}

const TeamEditDrawer = ({
  team,
  isOpen,
  onRequestClose,
  refetchTeam,
}: Props) => {
  const [activeTab, setActiveTab] = useState('details')
  const [displayAddMoreMembers, setDisplayAddMoreMembers] = useState(false)
  const [userRemountKey, setUserRemountKey] = useState(0)
  const [roleRemountKey, setRoleRemountKey] = useState(0)
  const makeToast = useToastAlert()

  const formMembers: Inputs['members'] = team?.members?.map((member: any) => ({
    id: member.id,
    firstName: member.firstName,
    lastName: member.lastName,
    companyName: member.companyName,
    lanId: member.lanId,
    isRemoved: false,
    roles: member?.roles?.map((role: any) => ({
      code: role.code.toString(),
      description: role.description,
    })),
  }))

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, dirtyFields, isDirty, isSubmitting, isValid },
  } = useForm<Inputs>({
    defaultValues: {
      teamName: team?.teamName || '',
      statusId: team?.status?.code,
      members: formMembers || [],
      newMember: null,
      newRoles: [],
    },
  })

  const handleCloseDrawer = useCallback(() => {
    reset()
    onRequestClose()
  }, [reset, onRequestClose])

  const currentMembers = watch('members')
  const currentFormName = watch('teamName')
  const currentNewMember = watch('newMember')
  const currentNewRoles = watch('newRoles')

  const handleRemoveMember = useCallback(
    (memberId: string) => () => {
      const updatedMembers = currentMembers.map((member) =>
        member.id === memberId ? { ...member, isRemoved: true } : member,
      )
      setValue('members', updatedMembers, { shouldDirty: true })
    },
    [currentMembers, setValue],
  )

  const handleUndoRemoveMember = useCallback(
    (memberId: string) => () => {
      const updatedMembers = currentMembers.map((member) =>
        member.id === memberId ? { ...member, isRemoved: false } : member,
      )
      setValue('members', updatedMembers, { shouldDirty: true })
    },
    [currentMembers, setValue],
  )

  const [updateTeam] = useMutation(UPDATE_TEAM_MUTATION)

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    const teamFormUpdates = extractDirtyFormValues({
      dirtyFields,
      values: data,
      customMappers: {
        members: (members: Inputs['members']): TeamUpdateInput['members'] =>
          members
            .filter((member) => !member.isRemoved)
            .map((member) => ({
              userProfileId: member.id,
              roles: member?.roles?.map((role) => Number(role.code)) || [],
            })),
      },
      fieldsToOmit: ['newMember', 'newRoles'],
    })

    const teamUpdateInput = {
      teamId: team?.id,
      teamType: team?.teamType,
      ...teamFormUpdates,
    }
    try {
      await updateTeam({ variables: { teamUpdateInput } })
      makeToast({ heading: 'Team updated successfully', variant: 'success' })
    } catch (updateError) {
      console.error('Error updating team:', updateError)
      makeToast({
        heading: 'Unable to update team',
        message:
          'We are unable to update the team at this time. Please try again or contact support.',
        variant: 'error',
      })
      // skip refetching the team if the update failed
      return
    }
    try {
      await refetchTeam?.()
    } catch (refetchError) {
      console.error('Error refetching team:', refetchError)
      makeToast({
        heading: 'Unable to load team',
        message:
          'The team was updated successfully, but we are unable to load the team at this time. Please try again or contact support.',
        variant: 'caution',
      })
    }
    handleCloseDrawer()
  }

  const handleUserSearchSelect = useCallback(
    (user: IOption & { data: any }) => {
      if (user) {
        setValue('newMember', user, { shouldDirty: false })
      } else {
        setValue('newMember', null, { shouldDirty: false })
      }
    },
    [setValue],
  )

  const handleRolesSelect = useCallback(
    (selectedRoles: Array<IOption>) => {
      setValue('newRoles', selectedRoles, { shouldDirty: false })
    },
    [setValue],
  )

  const handleAddMember = useCallback(() => {
    const existingMembers = currentMembers || []
    setUserRemountKey((prev) => prev + 1)
    setRoleRemountKey((prev) => prev + 1)
    setValue(
      'members',
      [
        ...existingMembers,
        {
          id: currentNewMember?.id || '',
          firstName: currentNewMember?.data?.firstName || '',
          lastName: currentNewMember?.data?.lastName || '',
          companyName: currentNewMember?.data?.companyName || '',
          isRemoved: false,
          lanId: currentNewMember?.data?.lanId || '',
          roles: currentNewRoles.map((role) => ({
            code: role.value,
            description: role.label,
          })),
        },
      ],
      { shouldDirty: true },
    )
    setValue('newMember', null, { shouldDirty: false })
    setValue('newRoles', [], { shouldDirty: false })
  }, [currentMembers, currentNewMember, currentNewRoles, setValue])

  const shouldDisplayRoleField = useMemo(() => {
    if (!currentNewMember) return false
    return currentNewMember.data.internal === true
  }, [currentNewMember])

  const userSearchScope = useMemo<UserSearchScope>(() => {
    if (team?.teamType === 'EXTERNAL' && team?.businessPartner?.id) {
      return {
        kind: 'targetAndPartner',
        partnerId: Number(team.businessPartner.id),
      }
    }
    return { kind: 'targetOnly' }
  }, [team])

  return (
    <Drawer
      isOpen={isOpen}
      onRequestClose={handleCloseDrawer}
      headingText="Edit Team"
      footerContent={
        <Button
          isDisabled={!isValid || !isDirty || isSubmitting}
          className="hc8-wth-100"
          variant="primary"
          onClick={handleSubmit(onSubmit)}
        >
          Save preferences
        </Button>
      }
    >
      <Grid.Container direction="column">
        <Grid.Item size={{ xs: 12 }} className="hc8-mv-3x">
          <Tabs activeTab={activeTab} onTabSelect={setActiveTab}>
            <Tabs.Item name="details">Details</Tabs.Item>
            <Tabs.Item name="members">Members</Tabs.Item>
          </Tabs>
        </Grid.Item>
        <Grid.Item size={{ xs: 12 }}>
          <Separator />
        </Grid.Item>
        <Grid.Item size={{ xs: 12 }} className="hc8-mv-3x">
          {activeTab === 'details' && (
            <Grid.Container direction="column">
              <Grid.Item>
                <ControlledFormField
                  control={control}
                  name="teamName"
                  rules={{
                    required: 'Team name is required',
                    maxLength: { value: 75, message: 'Max length is 75' },
                  }}
                  id="teamName"
                  label="Team name"
                  type="text"
                  isRequired
                  hintText={characterCount(currentFormName, 75)}
                  isErrored={!!errors.teamName}
                  errorText={errors.teamName?.message}
                />
              </Grid.Item>
              <Grid.Item>
                <ControlledFormField
                  control={control}
                  name="statusId"
                  id="statusId"
                  label="Status"
                  type="select"
                  isErrored={!!errors.statusId}
                  errorText={errors.statusId?.message}
                  options={[
                    { id: 1, label: 'Active', value: 1 },
                    {
                      id: 0,
                      label: 'Inactive',
                      value: 0,
                    },
                  ]}
                />
              </Grid.Item>
            </Grid.Container>
          )}
          {activeTab === 'members' && (
            <Grid.Container direction="column">
              <Grid.Item>
                {!displayAddMoreMembers && (
                  <Button
                    className="hc8-wth-100"
                    variant="secondary"
                    onClick={() => setDisplayAddMoreMembers(true)}
                  >
                    <Icon svg={PlusCircleIcon} /> Add another member
                  </Button>
                )}
                {displayAddMoreMembers && (
                  <Grid.Container direction="column">
                    <Grid.Item>
                      <UserSearchAutocomplete
                        key={userRemountKey}
                        label="Add a team member"
                        onUserSelect={handleUserSearchSelect}
                        scope={userSearchScope}
                      />
                    </Grid.Item>
                    {shouldDisplayRoleField && (
                      <Grid.Item>
                        <UserRolesCombobox
                          key={roleRemountKey}
                          className="hc8-mb-1x"
                          label={`Roles ${currentNewRoles?.length > 0 ? `(${currentNewRoles.length})` : ''}`}
                          placeholder="Search by role"
                          onRolesSelect={handleRolesSelect}
                        />
                      </Grid.Item>
                    )}
                    <Grid.Item>
                      <Button
                        className="hc8-wth-100"
                        variant="secondary"
                        onClick={handleAddMember}
                      >
                        <Icon svg={PlusCircleIcon} />
                        Add to team
                      </Button>
                    </Grid.Item>
                    <Grid.Item className="hc8-mt-2x">
                      <Separator />
                    </Grid.Item>
                  </Grid.Container>
                )}
              </Grid.Item>
              <Grid.Item>
                <Separator />
              </Grid.Item>
              {currentMembers.length > 0 && (
                <Grid.Item size={{ xs: 12 }}>
                  <TeamMemberList
                    teamMembers={currentMembers}
                    onUndoRemoveMember={handleUndoRemoveMember}
                    onRemoveMember={handleRemoveMember}
                  />
                </Grid.Item>
              )}
            </Grid.Container>
          )}
        </Grid.Item>
      </Grid.Container>
    </Drawer>
  )
}

export default TeamEditDrawer
